import React, { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Loader2, ShieldAlert } from "lucide-react";
import ReportedMessageRow from "./ReportedMessageRow";
import { reportHeatPct } from "../achQueueHelpers";

export default function ReportedMessagesIndex({
  items,
  selectedId,
  onSelect,
  loading,
  pagination,
  onPageChange,
}) {
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  const { page = 1, totalPages = 1, total = 0, limit = 20 } = pagination || {};
  const startIndex = (page - 1) * limit;
  const endIndex = Math.min(startIndex + items.length, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const channelHint = "Lounge";

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      const row = selectedRef.current;
      const list = listRef.current;
      const rowBox = row.getBoundingClientRect();
      const listBox = list.getBoundingClientRect();
      if (rowBox.top < listBox.top || rowBox.bottom > listBox.bottom) {
        row.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedId]);

  return (
    <aside className="amf-index ach-index" aria-label="Reports queue">
      <div className="amf-index-head ach-index-head">
        <div className="amf-index-head-row">
          <span className="amf-index-brand-label">Reports queue</span>
          <span className="amf-index-count">
            {total > 0 ? (
              <>
                <strong>
                  {startIndex + 1}–{endIndex}
                </strong>
                {" / "}
                <strong>{total}</strong>
              </>
            ) : (
              <strong>0</strong>
            )}
            {totalPages > 1 ? ` · p${page}` : ""}
          </span>
        </div>
        <p className="ach-index-hint">{channelHint} · select a row to review</p>
      </div>

      <div ref={listRef} className={`amf-index-list ach-index-list${loading ? " ach-index-list-loading" : ""}`}>
        {loading && !items.length ? (
          <div className="ach-index-state">
            <Loader2 size={18} className="am-spin" aria-hidden />
            <span>Loading reports…</span>
          </div>
        ) : null}

        {!loading && !items.length ? (
          <div className="ach-index-state ach-index-empty">
            <ShieldAlert size={26} strokeWidth={1.4} aria-hidden />
            <p className="ach-index-empty-title">Queue is clear</p>
            <p className="ach-index-empty-copy">No reported messages in this view.</p>
          </div>
        ) : null}

        {items.map((item, idx) => {
          const rank = startIndex + idx + 1;
          const selected = selectedId === item.id;
          return (
            <div key={item.id} ref={selected ? selectedRef : null}>
              <ReportedMessageRow
                item={item}
                rank={rank}
                selected={selected}
                onSelect={onSelect}
                heatPct={reportHeatPct(item)}
              />
            </div>
          );
        })}
      </div>

      {totalPages > 1 ? (
        <div className="ach-index-foot">
          <span className="ach-index-foot-meta">Page through open reports</span>
          <div className="ach-index-pager">
            <button
              type="button"
              className="ach-pager-btn"
              disabled={!canPrev || loading}
              onClick={() => onPageChange(page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} aria-hidden />
            </button>
            <span className="ach-pager-label tabular-nums">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              className="ach-pager-btn"
              disabled={!canNext || loading}
              onClick={() => onPageChange(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={16} aria-hidden />
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
